import React, {createContext, useCallback, useContext, useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
import {FormikProvider, useFormik} from 'formik';
import {debounce} from 'debounce';
import {FormattedMessage, useIntl} from 'react-intl';
import {format, parseISO} from 'date-fns';
import {createColumnHelper, getCoreRowModel, Table, useReactTable} from '@tanstack/react-table';
import Spinner from '../../../../common/components/ui/Spinner';
import {InfiniteScrollProps} from '../../../../common/types';
import {useMessage} from '../../../../common/components/core/useGlobalMessage';
import {useGlobalContext} from '../../../../common/components/core/GlobalContext';
import {useGlobalMessageContext} from '../../../../common/components/core/GlobalMessageContext';
import {ReceiptsFilterValues, ReceiptsItem} from '../../types';
import {downloadFile, Format} from '../../../../common/utils';
import {ExcelExportService} from '../../../../services';
import ColumnButtonDownload from '../../../../common/components/ui/ColumnButtonDownload';
import {RootState} from '../../../../store/configureStore';

type Option = { label: string, value: string };

export type ReceiptsGridContext = {
  receiptsList: ReceiptsItem[],
  totalCount: number,
  totalAmount: number,
  infiniteScroll: {
    containerID: string,
    props: InfiniteScrollProps
  },
  table: Table<ReceiptsItem>,
  saveListToExcelFile: () => void,
  loadOptions: (input: string) => Promise<Option[]>
}

export type ReceiptsGridContextProviderProps = {
  children: React.ReactNode,
}

export const defaultContext: ReceiptsGridContext = {
  receiptsList: [],
  totalCount: 0,
  totalAmount: 0,
  infiniteScroll: {
    containerID: 'scrollContainer',
    props: {
      next: () => {
      },
      hasMore: false,
      loader: null,
      dataLength: 0
    }
  },
  table: {} as Table<ReceiptsItem>,
  saveListToExcelFile: () => {
  },
  loadOptions: async () => {
    return [];
  }
};

export const defaultFilters: ReceiptsFilterValues = {
  fromDate: null,
  toDate: null,
  customerID: null,
};

export const ReceiptsGridContext = createContext<ReceiptsGridContext>(defaultContext);
ReceiptsGridContext.displayName = 'ReceiptsGridContext';

export const useReceiptsGridContext = () => useContext(ReceiptsGridContext);

export const ReceiptsGridContextProvider = ({children}: ReceiptsGridContextProviderProps) => {
  const M = useMessage();
  const {formatMessage} = useIntl();
  const {apiService, DEBOUNCE_DELAY_MS} = useGlobalContext();
  const globalMessage = useGlobalMessageContext();
  const [receiptsList, setReceiptsList] = useState<ReceiptsItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const formik = useFormik<ReceiptsFilterValues>({
    initialValues: {...defaultFilters},
    onSubmit: () => {
    },
    enableReinitialize: true,
  });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const updateList = async (
    page: number,
    fromDate: Date | null,
    toDate: Date | null,
    customerID: string | null,
    previousList: ReceiptsItem[]
  ) => {
    try {
      const result = await apiService.getReceiptsList(page, fromDate, toDate, customerID);

      if (result.errorCode < 0) {
        globalMessage.open(M.serverError);
      } else {
        setTotalAmount(result.body.totalAmount);
        setTotalCount(result.body.count);
        setReceiptsList([...previousList, ...result.body.list] as ReceiptsItem[]);
      }
    } catch (e) {
      console.error(e);
      globalMessage.open(M.serverError);
    }
  };
  const callDebouncedHandler = debounce(updateList, DEBOUNCE_DELAY_MS, false);
  const update = useCallback(callDebouncedHandler, []);

  useEffect(() => {
    setPage(1);
    setLoading(true);
    updateList(1, null, null, null, [])
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!loading) {
      setPage(1);
      update(1, formik.values.fromDate, formik.values.toDate, formik.values.customerID, []);
    }
  }, [formik.values.customerID, formik.values.toDate, formik.values.fromDate]);

  const currentLocale = useSelector((state: RootState) => state.i18n.locale);
  const columnHelper = createColumnHelper<ReceiptsItem>();
  const columns = [
    columnHelper.accessor('id', {
      cell: info => info.getValue(),
      header: () => <FormattedMessage id="receipts-col-receiptId" defaultMessage="Receipt ID"/>,
    }),
    columnHelper.accessor('typeName', {
      cell: info => currentLocale === 'he'
        ? info.getValue()
        : <FormattedMessage id={`receipts-col-type-${info?.row?.original?.type}`} defaultMessage=" "/>,
      header: () => <FormattedMessage id="receipts-col-typeName" defaultMessage="Type"/>,
    }),
    columnHelper.accessor('customer', {
      cell: info => {
        const firstName = info.getValue()?.name?.first || '';
        const lastName = info.getValue()?.name?.last || '';

        return [firstName, lastName].filter(Boolean).join(' ');
      },
      header: () => <FormattedMessage id="receipts-col-customer" defaultMessage="Customer"/>,
    }),
    columnHelper.accessor('createdAt', {
      cell: info => {
        const value = info.getValue() as any;

        let date: Date | null = null;

        if (!value) return '-';

        if (typeof value === 'string') {
          date = parseISO(value);
        } else if (value instanceof Date) {
          date = value;
        } else if (typeof value === 'object' && value?.$date) {
          date = new Date(value?.$date);
        }

        if (!date || isNaN(date.getTime())) return '-';

        return format(date, 'dd/MM/yyyy');
      },
      header: () => <FormattedMessage id="receipts-col-createdAt" defaultMessage="Created At"/>,
    }),
    columnHelper.accessor('amount', {
      cell: info => Boolean(info) && Format.displayFloatThousandsWithCommas(info.getValue()),
      header: () => <FormattedMessage id="receipts-col-amount" defaultMessage="Amount"/>,
    }),
    columnHelper.accessor('details', {
      cell: info => {
        const amount = Format.displayFloatThousandsWithCommas(info.row.original?.amount);
        const date = format(parseISO(info.row.original?.createdAt), 'dd/MM/yyyy');

        return (
          <div>
            <div>{amount}</div>
            <div>{date}</div>
          </div>
        );
      },
      header: () => <FormattedMessage id="receipts-col-details" defaultMessage="Details"/>,
    }),
    columnHelper.accessor('download', {
      cell: info => {
        return <ColumnButtonDownload onClick={() => downloadDocument(info.row.original.id)}/>;
      },
      header: () => <FormattedMessage id="receipts-col-download" defaultMessage="Download"/>
    })
  ];

  const table = useReactTable({
    data: receiptsList,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const next = () => {
    const nextPage = page + 1;
    updateList(nextPage, formik.values.fromDate, formik.values.toDate, formik.values.customerID, receiptsList)
      .then(() => setPage(nextPage))
      .catch(console.error);
  };
  const infiniteScrollProps: InfiniteScrollProps = {
    hasMore: receiptsList.length < totalCount,
    loader: <Spinner/>,
    dataLength: receiptsList.length,
    next, // should return any, not Promise<any>
    scrollThreshold: 0.9,
  };

  const saveListToExcelFile = async () => {
    try {
      const receiptID = formatMessage({id: 'receipts-col-receiptId', defaultMessage: 'Receipt'});
      const typeName = formatMessage({id: 'receipts-col-typeName', defaultMessage: 'Type'});
      const customerName = formatMessage({id: 'receipts-col-customer', defaultMessage: 'Customer'});
      const createdAt = formatMessage({id: 'receipts-col-createdAt', defaultMessage: 'Created at'});
      const amount = formatMessage({id: 'receipts-col-amount', defaultMessage: 'Amount'});

      const {body: {list}} = await apiService.getReceiptsListForExcel(
        formik.values.fromDate,
        formik.values.toDate,
        formik.values.customerID,
      );
      const data = [
        ...list.map(item => {
          const customerFirstName = item?.customer?.name?.first || '';
          const customerLastName = item?.customer?.name?.last || '';

          return {
            [receiptID]: item.id,
            [typeName]: currentLocale === 'he'
              ? item.typeName
              : formatMessage({id: `receipts-col-type-${item.type}`, defaultMessage: ' '}),
            [customerName]: [customerFirstName, customerLastName].filter(Boolean).join(' '),
            [createdAt]: format(parseISO(`${item.createdAt}`), 'dd/MM/yyyy'),
            [amount]: Boolean(item.amount)
              ? Format.formatFloatThousandsWithCommas(parseFloat(`${item.amount}`).toFixed(2))
              : '',
          };
        })
      ];
      const excelExport = new ExcelExportService();
      excelExport
        .addPage(data, '')
        .generateFile('קבלות.xlsx');

    } catch (e) {
      globalMessage.open(M.serverError);
    }
  };

  const loadOptions = async (input: string) => {
    try {
      const result = await apiService.getReceiptCustomerOptions(input);
      if (result.errorCode < 0) {
        globalMessage.open(M.serverError);
        return [];
      } else {
        return result.body.list;
      }
    } catch (e) {
      globalMessage.open(M.serverError);
      return [];
    }
  };

  const downloadDocument = async (receiptID: string) => {
    try {
      const {body: {link}, errorCode} = await apiService.getReceiptDocument(receiptID);
      if (errorCode < 0) {
        globalMessage.open(M.serverError);
      } else {
        downloadFile(link, `קבלה - ${receiptID}`);
      }
    } catch (e) {
      console.error(e);
      globalMessage.open(M.serverError);
    }
  };

  const context: ReceiptsGridContext = {
    ...defaultContext,
    receiptsList,
    totalCount,
    totalAmount,
    table,
    infiniteScroll: {
      containerID: defaultContext.infiniteScroll.containerID,
      props: infiniteScrollProps
    },
    saveListToExcelFile,
    loadOptions,
  };

  return loading
    ? <Spinner/>
    : (
      <ReceiptsGridContext.Provider value={context}>
        <FormikProvider value={formik}>
          {children}
        </FormikProvider>
      </ReceiptsGridContext.Provider>
    );
};